import React from 'react';
import { observer } from 'mobx-react-lite';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';

export interface Option<T = string | number> {
  value: T;
  label: string;
}

interface CommonDropdownProps<T = string | number> {
  options: Option<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
}

const CommonDropdown = observer(
  <T extends string | number>({
    options,
    value,
    onChange,
    label,
    placeholder,
    error,
    className,
    disabled,
  }: CommonDropdownProps<T>) => {
    const selectId =
      label || placeholder
        ? `${(label || placeholder)!.replace(/\s+/g, '-').toLowerCase()}-select`
        : undefined;

    const muiValue = value === null || value === undefined ? '' : value.toString();

    return (
      <FormControl
        fullWidth
        error={!!error}
        className={className}
        disabled={disabled}
        variant="outlined"
        size="small"
      >
        {(label || placeholder) && (
          <InputLabel id={`${selectId}-label`}>{label || placeholder}</InputLabel>
        )}
        <Select
          labelId={`${selectId}-label`}
          id={selectId}
          value={muiValue}
          onChange={e => {
            const newValue = e.target.value;
            if (newValue === '') {
              onChange(null);
            } else {
              const typedValue =
                typeof options[0]?.value === 'number' ? Number(newValue) : newValue;
              onChange(typedValue as T);
            }
          }}
          label={label || placeholder}
        >
          {options.map(option => (
            <MenuItem key={option.value?.toString()} value={option.value?.toString()}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }
);

export default CommonDropdown;
